import type { VehicleByRegistrationNumber } from '@//app/api/protected/vehicle/lookup/registration-number/[registrationNumber]';
import type { ElasticRetailer } from '@//app/api/retailers/[slug]';
import type { ElasticListVehicle, ElasticListVehicleBranch } from '@//app/api/search/vehicles';
import type { ElasticVehicle, ElasticVehicleBranch } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { odometerReadingUnitByMarket } from '@//utils/odometer';
import type {
  LegacyEventItem,
  LegacyEventItemVehicleByRegistrationNumber,
  LegacyEventListItem,
} from './legacy-types';

const mapBranchDetails = (vehicleBranch: ElasticVehicleBranch, vehicle: ElasticVehicle) => {
  const leasingPromos = vehicle.leasingPromos?.[0];

  if (!vehicleBranch) return [];
  return [
    {
      city: vehicleBranch.city,
      id: vehicleBranch.id,
      name: vehicleBranch.name,
      authorizedResellerForManufacturer: vehicleBranch.authorizedResellerForManufacturer,
      ...(leasingPromos
        ? {
            leasing: true,
            'service-agreement': leasingPromos.serviceIncluded,
            'dealer-car-insurance': leasingPromos.insuranceIncluded,
            'other-services': leasingPromos.description,
          }
        : {}),
    },
  ];
};

const mapOdometerReading = (
  market: Market,
  odometerReading: ElasticVehicle['odometerReading'] | ElasticListVehicle['odometerReading'],
): { value: number; unit: 'km' | 'mil' } => {
  return {
    value: odometerReading?.value ?? 0,
    unit: ['ScandinavianMile', 'ScandinavianMiles'].includes(
      odometerReading ? odometerReading.unit : odometerReadingUnitByMarket[market],
    )
      ? 'mil'
      : 'km',
  };
};

export const mapElasticVehicleToLegacyItem = (
  market: Market,
  vehicle: ElasticVehicle,
): LegacyEventItem => {
  const vehicleBranch = vehicle.branches?.[0];

  const financialOptions = vehicle.financialOptions?.map((fo) => fo.financialInstitution) ?? [];
  const insuranceOptions = vehicle.insuranceOptions?.map((io) => io.institute) ?? [];

  const item: LegacyEventItem = {
    id: vehicle.id,
    manufacturer: vehicle.manufacturer,
    model: vehicle.modelSeries,
    price: vehicle.price,
    manufactureYear: vehicle.manufactureYear,
    fuelType: vehicle.fuelType,
    mileage: vehicle.mileage,
    odometerReading: mapOdometerReading(market, vehicle.odometerReading),
    gearboxType: vehicle.gearboxType,
    horsepower: vehicle.enginePower,
    segment: vehicle.properties?.segment,
    branches: vehicle.branches?.map((b) => b.id) ?? [],
    branchDetails: mapBranchDetails(vehicleBranch, vehicle),
    sellerType: 'branch',
  };

  if (financialOptions.length > 0) {
    item.financialOptions = financialOptions;
  }

  if (insuranceOptions.length > 0) {
    item.insuranceOptions = insuranceOptions;
  }

  return item;
};

const mapBranchDetailsListVehicle = (vehicleBranch: ElasticListVehicleBranch) => {
  if (!vehicleBranch) return [];
  return [
    {
      id: vehicleBranch.id,
      name: vehicleBranch.name,
    },
  ];
};

export const mapElasticListVehicleToLegacyItem = (
  market: Market,
  vehicle: ElasticListVehicle,
): LegacyEventListItem => {
  const vehicleBranch = vehicle.branches?.[0];

  const item: LegacyEventListItem = {
    id: vehicle._id,
    manufacturer: vehicle.manufacturer,
    model: vehicle.modelSeries,
    price: vehicle.price,
    fuelType: vehicle.fuelType,
    odometerReading: mapOdometerReading(market, vehicle.odometerReading),
    gearboxType: vehicle.gearboxType,
    branches: vehicle.branches?.map((b) => b.id) ?? [],
    branchDetails: mapBranchDetailsListVehicle(vehicleBranch),
    sellerType: 'dealer',
  };

  return item;
};

export const mapElasticBranchToLegacyItem = (vehicleBranch: ElasticRetailer) => {
  return {
    branchDetails: [{ name: 'private' }],
    id: vehicleBranch.id,
    sellerType: 'private',
  };
};

export const mapVehicleByRegistrationNumberToLegacyItem = (
  vehicle: VehicleByRegistrationNumber,
): LegacyEventItemVehicleByRegistrationNumber => {
  const item: LegacyEventItemVehicleByRegistrationNumber = {
    manufacturer: vehicle.manufacturer,
    model: vehicle.modelSeries,
    manufactureYear: vehicle.manufactureYear,
    fuelType: vehicle.fuelType,
    gearboxType: vehicle.gearboxType,
    horsepower: vehicle.enginePower,
    segment: vehicle.segment,
    branchDetails: [{ name: 'private' }],
    sellerType: 'private',
  };

  return item;
};

/**
 * Maps either ElasticVehicle or VehicleByRegistrationNumber to the appropriate legacy item type.
 * Detects the vehicle type by checking for the 'id' property (present in ElasticVehicle).
 */
export const mapElasticVehicleOrVehicleByRegistrationNumberToLegacyItem = (
  market: Market,
  vehicle: ElasticVehicle | VehicleByRegistrationNumber,
): LegacyEventItem | LegacyEventItemVehicleByRegistrationNumber => {
  // Check if it's an ElasticVehicle by checking for the 'id' property
  if ('id' in vehicle && vehicle.id) {
    return mapElasticVehicleToLegacyItem(market, vehicle as ElasticVehicle);
  }
  return mapVehicleByRegistrationNumberToLegacyItem(vehicle as VehicleByRegistrationNumber);
};

export const mapElasticVehicleOrElasticListVehicleToLegacyItem = (
  market: Market,
  vehicle: ElasticVehicle | ElasticListVehicle,
): LegacyEventItem | LegacyEventListItem => {
  // Check if it's an ElasticVehicle by checking for the 'id' property
  if ('id' in vehicle && vehicle.id) {
    return mapElasticVehicleToLegacyItem(market, vehicle as ElasticVehicle);
  }
  return mapElasticListVehicleToLegacyItem(market, vehicle as ElasticListVehicle);
};

/**
 * Maps any of the three vehicle types (ElasticVehicle, ElasticListVehicle, or VehicleByRegistrationNumber)
 * to the appropriate legacy item type.
 *
 * Distinguishing properties:
 * - ElasticVehicle: has 'id' property
 * - ElasticListVehicle: has '_id' property
 * - VehicleByRegistrationNumber: has neither 'id' nor '_id'
 */
export const mapAnythingToLegacyItem = (
  market: Market,
  vehicle: ElasticVehicle | ElasticListVehicle | VehicleByRegistrationNumber,
): LegacyEventItem | LegacyEventListItem | LegacyEventItemVehicleByRegistrationNumber => {
  // Check if it's an ElasticVehicle by checking for the 'id' property
  if ('id' in vehicle && vehicle.id) {
    return mapElasticVehicleToLegacyItem(market, vehicle as ElasticVehicle);
  }
  // Check if it's an ElasticListVehicle by checking for the '_id' property
  if ('_id' in vehicle && vehicle._id) {
    return mapElasticListVehicleToLegacyItem(market, vehicle as ElasticListVehicle);
  }
  // Otherwise, it's a VehicleByRegistrationNumber
  return mapVehicleByRegistrationNumberToLegacyItem(vehicle as VehicleByRegistrationNumber);
};
